import S from './CompanyEditor.module.scss';
import {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {AdminCompanyEditResponseType, AdminCompanyInfoType} from '../../../types/AdminTypes';
import { uploadImage } from '../../../repository/ResumeRepository';
import _ from 'lodash';
import {
    deleteAdminCompanyList,
    editAdminCompanyList, getAdminCompany,
    registerAdminCompany,
    uploadAdminImage
} from '../../../repository/AdminRepository';
import {ErrorType} from "../../../types/ErrorType";

interface CompanyEditorProps {
    mode: 'register' | 'editor';
    setShowCompanyEditor: Dispatch<SetStateAction<boolean>>;
    id?: number;
}

const CompanyEditor = (props: CompanyEditorProps) => {
  const { mode, setShowCompanyEditor, id } = props;
  const ADMIN_TOKEN = process.env.REACT_APP_IVY_ADMIN_TOKEN || '';
  const [companyInfo, setCompanyInfo] = useState<AdminCompanyInfoType>({} as AdminCompanyInfoType);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    setCompanyInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadFile = async (file: File) => {
    await uploadAdminImage(ADMIN_TOKEN || '', file)
      .then((res) => {
        if (!_.isEmpty(res.mediaUrl)) {
          setCompanyInfo((prev) => ({
            ...prev,
            imageUrl: res.mediaUrl,
          }));
          setImagePreview(res.mediaUrl);
        } else {
          alert('이미지 업로드 에러!');
        }
      })
      .catch((e) => console.error(e));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      uploadFile(event.target.files[0]);
      event.target.value = ''; // 두번쨰 업로드를 위해 초기화 시켜준다.
    }
  };

  const handleSubmit = (mode: 'register' | 'edit' | 'delete') => {
    if (mode === 'register') {
      registerAdminCompany(companyInfo, ADMIN_TOKEN)
        .then((res) => {
          if ((res as AdminCompanyEditResponseType).success !== undefined) {
            setCompanyInfo({} as AdminCompanyInfoType);
            setImagePreview('');
            setShowCompanyEditor(false);
          }else{
              alert((res as ErrorType).message);
          }
        })
        .catch((e) => {
            console.error(e);
        });
    } else if (mode === 'edit') {
      if (id !== undefined) {
        editAdminCompanyList(id.toString(), companyInfo, ADMIN_TOKEN)
          .then((res) => {
            if (res.success) {
              setCompanyInfo({} as AdminCompanyInfoType);
              setImagePreview('');
              setShowCompanyEditor(false);
            }
          })
          .catch((e) => console.error(e));
      }
    } else if (mode === 'delete') {
      if (id !== undefined) {
        deleteAdminCompanyList(id.toString(), ADMIN_TOKEN)
          .then((res) => {
            if (res.success) {
              setCompanyInfo({} as AdminCompanyInfoType);
              setImagePreview('');
              setShowCompanyEditor(false);
            }
          })
          .catch((e) => console.error(e));
      }
    }
  };

  useEffect(() => {
    if (id !== undefined && mode === 'editor') {
      getAdminCompany(id.toString(), ADMIN_TOKEN)
        .then((res) => {
          if (res) {
            setCompanyInfo(res);
            if (res.imageUrl) {
              setImagePreview(res.imageUrl);
            }
          }
        })
        .catch((e) => console.error(e));
    }
  }, [id]);

  return (
    <div className={S.companyRegistration}>
      <div className={S.backButtonContainer}>
        <button type="button" onClick={() => setShowCompanyEditor(false)} className={S.backButton}>
          X
        </button>
      </div>
      <CompanyRegisterField
        name={'name'}
        label={'회사 명'}
        value={companyInfo.name || ''}
        changeFn={handleChange}
        required={true}
      />
      <CompanyRegisterField
        name={'displayName'}
        label={'회사 노출명'}
        value={companyInfo.displayName || ''}
        changeFn={handleChange}
        required={true}
      />
      <CompanyRegisterField
        name={'businessNo'}
        label={'사업자번호'}
        value={companyInfo.businessNo || ''}
        changeFn={handleChange}
        required={true}
      />
      <CompanyRegisterField
        name={'representativeName'}
        label={'대표자명'}
        value={companyInfo.representativeName || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'industry'}
        label={'업종'}
        value={companyInfo.industry || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'description'}
        label={'설명'}
        value={companyInfo.description || ''}
        changeFn={handleChange}
        required={true}
      />
      <CompanyRegisterField
        name={'address'}
        label={'주소'}
        value={companyInfo.address || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'scale'}
        label={'회사 규모'}
        value={companyInfo.scale || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'capitalAmount'}
        label={'자본금'}
        value={companyInfo.capitalAmount || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'yearSalesAmount'}
        label={'연 매출액'}
        value={companyInfo.yearSalesAmount || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'workerCount'}
        label={'근로자 수'}
        value={companyInfo.workerCount || ''}
        changeFn={handleChange}
        required={false}
      />
      <CompanyRegisterField
        name={'homepage'}
        label={'홈페이지'}
        value={companyInfo.homepage || ''}
        changeFn={handleChange}
        required={false}
      />
      <div className={S.field}>
        <div className={S.labelContainer}>
          <div className={S.label}>{'필터 사용여부'}</div>
          <input
            type="checkbox"
            name={'usedFilter'}
            className={S.checkboxCustom}
            checked={companyInfo.usedFilter}
            onChange={(e) => {
              setCompanyInfo((prev) => ({ ...prev, usedFilter: e.target.checked }));
            }}
          />
        </div>
      </div>
      <div className={S.field}>
        <div className={S.labelContainer}>
          <div className={S.label}>회사 이미지</div>
        </div>
        <input
          type="file"
          name="imageUpload"
          accept={'jpeg'}
          onChange={(e) => handleImageChange(e)}
        />
        {imagePreview && <img src={imagePreview} alt="Company preview" className="image-preview" />}
      </div>
      {mode === 'register' && (
        <button type="button" onClick={(e) => handleSubmit('register')}>
          등록
        </button>
      )}
      {mode === 'editor' && (
        <div className={S.actionButtons}>
          <button className={S.editButton} type="button" onClick={(e) => handleSubmit('edit')}>
            수정
          </button>
          <button className={S.deleteButton} type="button" onClick={(e) => handleSubmit('delete')}>
            삭제
          </button>
        </div>
      )}
    </div>
  );
};

interface CompanyRegisterFieldProps {
  name: string;
  label: string;
  value: string;
  required: boolean;
  changeFn: Function;
}

const CompanyRegisterField = (props: CompanyRegisterFieldProps) => {
  const { name, label, value, changeFn, required } = props;

  return (
    <div className={S.field}>
      <div className={S.labelContainer}>
        <div className={S.label}>{label}</div>
        {required && <span className={S.required}>*</span>}
      </div>
      <input type="text" name={name} value={value} onChange={(e) => changeFn(e)} />
    </div>
  );
};

export default CompanyEditor;
